import React, { useEffect, useState } from "react";
import { Plug, CheckCircle2, Clock, AlertTriangle, Loader2, RefreshCw, Unplug, KeyRound, Copy, Mail, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  getIntegrations, connectServiceTitan, disconnectServiceTitan, testServiceTitan, syncIntegrationsNow,
  setCertificateEmail, createApiKey, revokeApiKey, formatApiError,
} from "@/auth";

const copy = async (text, label) => {
  try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); } catch { toast.info(text); }
};

const StatusPill = ({ status }) => {
  const map = {
    connected: ["bg-emerald-500/15 text-emerald-300", CheckCircle2, "Connected"],
    pending_activation: ["bg-amber-500/15 text-amber-300", Clock, "Pending activation"],
    error: ["bg-rose-500/15 text-rose-300", AlertTriangle, "Needs attention"],
  };
  const [cls, Icon, label] = map[status] || map.pending_activation;
  return <span data-testid="st-status" className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${cls}`}><Icon className="h-3 w-3" /> {label}</span>;
};

const Input = (props) => (
  <input {...props} className="w-full rounded-lg border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-orange-500" />
);

export const ServiceTitanCard = ({ data, reload, canEdit, compact }) => {
  const st = data.servicetitan;
  const [form, setForm] = useState({ st_tenant_id: "", app_key: "" });
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(!st);
  useEffect(() => { setEditing(!st); }, [st]);

  const save = async (e) => {
    e.preventDefault(); setBusy("save");
    try { await connectServiceTitan({ ...form, auto_sync: true }); toast.success("ServiceTitan details saved"); setForm({ st_tenant_id: "", app_key: "" }); reload(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); }
  };
  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); toast[r?.ok === false ? "info" : "success"](r?.message || r?.summary || okMsg); reload(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="servicetitan-card" className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="font-display text-base font-semibold text-slate-100">ServiceTitan</span>
          {st && <StatusPill status={st.status} />}
        </div>
        {st && canEdit && (
          <div className="flex flex-wrap gap-2">
            <button data-testid="st-test-button" onClick={() => run("test", testServiceTitan)} disabled={!!busy} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50">
              {busy === "test" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />} Test
            </button>
            <button data-testid="st-sync-button" onClick={() => run("sync", syncIntegrationsNow, "Sync complete")} disabled={!!busy || !data.servicetitan_platform_ready} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-50">
              {busy === "sync" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync now
            </button>
            <button data-testid="st-edit-button" onClick={() => setEditing((v) => !v)} className="rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs text-slate-300 hover:bg-slate-800">{editing ? "Cancel" : "Edit"}</button>
            <button data-testid="st-disconnect-button" onClick={() => window.confirm("Disconnect ServiceTitan? Automatic job sync and certificate write-back will stop for this company.") && run("off", disconnectServiceTitan, "ServiceTitan disconnected")} disabled={!!busy} className="inline-flex items-center gap-1 rounded-lg border border-rose-500/40 px-2.5 py-1.5 text-xs text-rose-300 hover:bg-rose-500/10 disabled:opacity-50">
              <Unplug className="h-3.5 w-3.5" /> Disconnect
            </button>
          </div>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-400">{data.providers.find((p) => p.key === "servicetitan")?.blurb} Runs automatically every {data.sync_hours} hours.</p>
      {st && !data.servicetitan_platform_ready && (
        <div data-testid="st-pending-note" className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          Your details are saved. Live sync switches on automatically the moment R.U.A.C.H.'s ServiceTitan developer app is approved — nothing else to do on your side.
        </div>
      )}
      {st && st.last_error && <p data-testid="st-last-error" className="mt-2 text-xs text-rose-300">{st.last_error}</p>}
      {st && !editing && (
        <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
          <div><dt className="text-slate-500">Tenant ID</dt><dd data-testid="st-tenant-id" className="font-mono text-slate-200">{st.st_tenant_id}</dd></div>
          <div><dt className="text-slate-500">App key</dt><dd className="font-mono text-slate-200">{st.app_key_masked}</dd></div>
          <div><dt className="text-slate-500">Last sync</dt><dd className="text-slate-200">{st.last_sync_at ? new Date(st.last_sync_at).toLocaleString() : "Not yet"}{st.jobs_imported ? ` · ${st.jobs_imported} jobs imported` : ""}</dd></div>
        </dl>
      )}
      {editing && canEdit && (
        <form onSubmit={save} className="mt-3 grid gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
          <Input data-testid="st-tenant-input" value={form.st_tenant_id} onChange={(e) => setForm({ ...form, st_tenant_id: e.target.value })} placeholder="ServiceTitan Tenant ID" required />
          <Input data-testid="st-appkey-input" type="password" value={form.app_key} onChange={(e) => setForm({ ...form, app_key: e.target.value })} placeholder="Application Key" required />
          <button data-testid="st-save-button" type="submit" disabled={busy === "save"} className="inline-flex items-center justify-center gap-1 rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-500 disabled:opacity-60">
            {busy === "save" && <Loader2 className="h-4 w-4 animate-spin" />} {st ? "Update" : "Connect"}
          </button>
          <p className="text-[11px] text-slate-500 sm:col-span-3">Find both in ServiceTitan under Settings → Integrations → API Application Access. The Tenant ID is the number shown at the top of that page.</p>
        </form>
      )}
      {!compact && st && data.sync_log?.length > 0 && (
        <ul data-testid="st-sync-log" className="mt-3 space-y-1 border-t border-slate-800 pt-3 text-[11px] text-slate-400">
          {data.sync_log.map((e, i) => <li key={i}><span className="text-slate-600">{new Date(e.ts).toLocaleString()}</span> · {e.trigger} · <span className={e.ok ? "text-emerald-300" : "text-rose-300"}>{e.summary}</span></li>)}
        </ul>
      )}
    </div>
  );
};

export const ComingSoonGrid = ({ providers }) => (
  <div className="grid gap-2 sm:grid-cols-2" data-testid="coming-soon-grid">
    {providers.filter((p) => !p.available).map((p) => (
      <div key={p.key} data-testid={`provider-${p.key}`} className="flex items-center justify-between rounded-xl border border-dashed border-slate-800 px-4 py-3">
        <span className="text-sm text-slate-300">{p.label}</span>
        <span className="rounded-md bg-slate-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Coming soon</span>
      </div>
    ))}
  </div>
);

export const CertificateEmailCard = ({ data, reload, canEdit }) => {
  const [email, setEmail] = useState(data.certificate_email || "");
  const [enabled, setEnabled] = useState(data.email_certificates);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setEmail(data.certificate_email || ""); setEnabled(data.email_certificates); }, [data]);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await setCertificateEmail({ certificate_email: email || null, enabled }); toast.success(enabled ? "Certificates will be emailed on registration" : "Certificate emails turned off"); reload(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="certificate-email-card" className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-orange-400" /><span className="font-display text-base font-semibold text-slate-100">Email certificates to your inbox</span></div>
      <p className="mt-1 text-xs text-slate-400">Each time a customer is registered, the warranty PDF(s) are emailed to this address so your team can attach them in any system.</p>
      <form onSubmit={save} className="mt-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 text-sm text-slate-300">
          <input data-testid="cert-email-toggle" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={!canEdit} className="h-4 w-4 accent-orange-500" /> Enabled
        </label>
        <div className="min-w-[240px] flex-1"><Input data-testid="cert-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="warranties@yourcompany.com" disabled={!canEdit} /></div>
        {canEdit && <button data-testid="cert-email-save" type="submit" disabled={busy} className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-600 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</button>}
      </form>
    </div>
  );
};

export const ApiKeyCard = ({ data, reload, canEdit }) => {
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState("");
  const base = process.env.REACT_APP_BACKEND_URL;
  const gen = async () => {
    if (data.api_key && !window.confirm("Rotate the API key? The current key stops working immediately and any system using it must be updated.")) return;
    setBusy(true);
    try { const r = await createApiKey(); setReveal(r.plain_key); reload(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Revoke the API key? Systems using it will be rejected until a new key is generated.")) return;
    setBusy(true);
    try { await revokeApiKey(); setReveal(""); toast.success("API key revoked"); reload(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="api-key-card" className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-orange-400" /><span className="font-display text-base font-semibold text-slate-100">Push jobs from any system (API key)</span></div>
        {canEdit && (
          <div className="flex gap-2">
            <button data-testid="api-key-generate" onClick={gen} disabled={busy} className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-600 disabled:opacity-60">{data.api_key ? "Rotate key" : "Generate key"}</button>
            {data.api_key && <button data-testid="api-key-revoke" onClick={revoke} disabled={busy} className="inline-flex items-center gap-1 rounded-lg border border-rose-500/40 px-3 py-1.5 text-xs text-rose-300 hover:bg-rose-500/10 disabled:opacity-60"><Trash2 className="h-3.5 w-3.5" /> Revoke</button>}
          </div>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-400">For FSMs we don't connect to natively yet, or tools like Zapier/Make: your system sends completed installs to R.U.A.C.H. and reads back status and certificates.</p>
      {reveal && (
        <div data-testid="api-key-reveal" className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-200">
          <p className="font-semibold">Copy this key now — it is shown only once.</p>
          <div className="mt-1 flex items-center gap-2"><code className="flex-1 break-all font-mono text-emerald-100">{reveal}</code><button data-testid="api-key-copy" onClick={() => copy(reveal, "API key")} className="rounded-md border border-emerald-500/40 p-1.5 hover:bg-emerald-500/20"><Copy className="h-3.5 w-3.5" /></button></div>
        </div>
      )}
      {data.api_key && !reveal && <p data-testid="api-key-meta" className="mt-2 text-xs text-slate-400">Active key <span className="font-mono text-slate-200">{data.api_key.prefix}…</span> · created {new Date(data.api_key.created_at).toLocaleDateString()} by {data.api_key.created_by}{data.api_key.last_used_at ? ` · last used ${new Date(data.api_key.last_used_at).toLocaleString()}` : " · not used yet"}</p>}
      <details className="mt-3 text-xs text-slate-400">
        <summary className="cursor-pointer text-slate-300 hover:text-orange-300">How to send jobs (developer notes)</summary>
        <pre className="mt-2 overflow-x-auto rounded-lg bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-slate-300">{`POST ${base}/api/v1/jobs
X-API-Key: <your key>
Content-Type: application/json

{"jobs":[{"job_number":"12345","first_name":"Jane","last_name":"Doe",
  "address1":"100 Main St","city":"Dallas","state":"TX","zip":"75201",
  "install_date":"06/15/2026",
  "units":[{"model":"4TTR4036L1000A","serial":"24123ABCDE"}]}],
 "register_now":true}

GET ${base}/api/v1/jobs/12345            → status + certificate links
GET ${base}/api/v1/jobs/12345/certificate?part=trane → PDF`}</pre>
      </details>
    </div>
  );
};

export default function IntegrationsModal({ open, onClose, me }) {
  const [data, setData] = useState(null);
  const canEdit = me.role === "owner" || me.role === "admin";
  const load = () => getIntegrations().then(setData).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (open) load(); }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="integrations-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl border border-slate-700 bg-[#161f32] shadow-2xl fade-up">
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-slate-100"><Plug className="h-5 w-5 text-orange-400" /> Integrations</h2>
          <button data-testid="integrations-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200">✕</button>
        </div>
        <div className="space-y-4 overflow-y-auto px-6 py-5">
          {!data ? <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
            <>
              <ServiceTitanCard data={data} reload={load} canEdit={canEdit} />
              <ComingSoonGrid providers={data.providers} />
              <CertificateEmailCard data={data} reload={load} canEdit={canEdit} />
              <ApiKeyCard data={data} reload={load} canEdit={canEdit} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
